import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';

/**
 * KioskLanguageToggle
 *
 * The one control that switches the WHOLE kiosk between English and French.
 * Mounted once by KioskLayout so it is present on every /kiosk/* route
 * (check-in flow, QR scanner, payment result) rather than per-screen.
 *
 * Why it lives here and not in the flow:
 *   - Every kiosk screen reads its copy from the `kiosk.*` i18n namespace, so
 *     flipping `i18n.language` re-renders the entire surface at once. There is
 *     no per-screen wiring to keep in sync.
 *   - The choice must be reachable at ANY point in the flow (a customer may
 *     only realise mid-check-in), not just on the landing screen.
 *
 * Persistence: i18next's LanguageDetector is configured with
 * `caches: ['localStorage']` (see src/i18n.js), so the selection survives a
 * kiosk reload / browser restart. It is only ever changed by an explicit tap
 * — the kiosk does NOT auto-revert between customers, so whichever language
 * was last chosen stays until someone taps the other one.
 *
 * The chosen locale is ALSO what the kiosk threads to the backend as
 * `preferredLanguage` on booking creation (KioskLoginPage / KioskScannerPage),
 * which is what filters the waiver DocumentBundle's en/fr templates. So a
 * customer who taps FR here gets a French waiver too, not just French chrome.
 */

// Native names, deliberately NOT translated — a French speaker looks for
// "Français", not "French". Standard practice for language pickers.
const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
];

const KioskLanguageToggle = ({ className = '' }) => {
  const { i18n, t } = useTranslation();
  // i18n.language can carry a region subtag (e.g. 'en-CA' from the browser
  // detector); compare on the base tag so the right pill reads as active.
  const active = (i18n.language || 'en').split('-')[0];

  return (
    <div
      className={`flex items-center gap-1 rounded-full border border-border/70 bg-white/95 p-1 shadow-md backdrop-blur-sm ${className}`}
      role="group"
      aria-label={t('kiosk.language.label')}
    >
      <Languages className="ml-1.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      {LANGUAGES.map((lang) => {
        const isActive = active === lang.code;
        return (
          <button
            key={lang.code}
            type="button"
            onClick={() => i18n.changeLanguage(lang.code)}
            aria-pressed={isActive}
            // The visible short code (EN/FR) is unambiguous next to the globe
            // icon and keeps the chip small; the full native name goes to
            // screen readers so the control is never just two letters there.
            aria-label={t('kiosk.language.switchTo', { language: lang.label })}
            className={`rounded-full px-3 text-sm font-bold tracking-wide transition-colors ${
              isActive
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {lang.short}
          </button>
        );
      })}
    </div>
  );
};

export default KioskLanguageToggle;
